import { Emitter } from '@patsnap/tahiti-core';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { useDisposes } from '@telecom/shared/composables/use-disposes';
import { RavenService, type RavenTrackEvent } from '@telecom/shared/services/raven-service/raven-service';
import { createSharedComposable, useStorage } from '@vueuse/core';
import { readonly, ref, watch } from 'vue';

/**
 * 埋点调试开关的 localStorage key。
 */
export const TRACK_EVENT_DEBUG_STORAGE_KEY = 'drafting:track-event-debug';

/**
 * 埋点调试面板展开状态的 localStorage key。
 */
export const TRACK_EVENT_DEBUG_EXPANDED_STORAGE_KEY = 'drafting:track-event-debug-expanded';

/**
 * 埋点调试面板最多保留的事件数量。
 */
export const TRACK_EVENT_DEBUG_MAX_RECORDS = 100;

/**
 * 埋点详情中不在调试面板展示的通用字段。
 */
export const TRACK_EVENT_COMMON_DETAIL_KEYS = new Set([
    'userLevel',
    'signatureId',
    'product_line',
    'agent_name',
    'agent_namep',
    'pageUrl',
    'campaignName',
]);

/**
 * 埋点调试面板中的一条记录。
 */
export interface TrackEventDebugRecord {
    /**
     * 埋点请求组装完成的时间戳。
     */
    timestamp: number;
    /**
     * 最终埋点请求。
     */
    request: RavenTrackEvent['request'];
}

function isTrackEventDetailRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}

function formatParsedTrackEventDetail(value: unknown, includeCommonFields: boolean): string {
    if (!isTrackEventDetailRecord(value)) {
        return JSON.stringify(value, null, 2) || '—';
    }

    const displayValue = includeCommonFields
        ? value
        : Object.fromEntries(
            Object.entries(value).filter(([key]) => !TRACK_EVENT_COMMON_DETAIL_KEYS.has(key)),
        );

    return Object.keys(displayValue).length
        ? JSON.stringify(displayValue, null, 2) || '—'
        : '—';
}

/**
 * 格式化埋点详情字符串。
 *
 * 合法 JSON 会格式化为易读文本，普通字符串或非法 JSON 保留原值。
 */
export function formatTrackEventDetail(value?: string, includeCommonFields = false): string {
    if (!value) {
        return '—';
    }

    try {
        return formatParsedTrackEventDetail(JSON.parse(value), includeCommonFields);
    } catch {
        return value;
    }
}

/**
 * 格式化埋点时间戳，精确到毫秒。
 */
export function formatTrackEventTimestamp(timestamp: number): string {
    const date = new Date(timestamp);
    const pad = (value: number, length = 2) => String(value).padStart(length, '0');

    return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${pad(date.getMilliseconds(), 3)}`;
}

/**
 * 将新的埋点记录放到时间线末尾，并限制记录数量。
 */
export function appendTrackEventRecord(
    records: TrackEventDebugRecord[],
    event: RavenTrackEvent,
): TrackEventDebugRecord[] {
    return [
        ...records,
        {
            timestamp: event.timestamp,
            request: { ...event.request },
        },
    ].slice(-TRACK_EVENT_DEBUG_MAX_RECORDS);
}

export const useTrackEventDebug = createSharedComposable(() => {
    const ravenService = injectService<RavenService>(RavenService);
    const enabled = useStorage(TRACK_EVENT_DEBUG_STORAGE_KEY, false);
    const records = ref<TrackEventDebugRecord[]>([]);
    const isExpanded = useStorage(TRACK_EVENT_DEBUG_EXPANDED_STORAGE_KEY, true);
    const { disposes } = useDisposes();
    const onRecordEmitter = new Emitter<TrackEventDebugRecord>();
    let activeSubscription: { dispose: () => void } | undefined;

    function appendRecord(event: RavenTrackEvent) {
        records.value = appendTrackEventRecord(records.value, event);
        onRecordEmitter.fire({
            timestamp: event.timestamp,
            request: { ...event.request },
        });
    }

    function subscribe() {
        if (!enabled.value || activeSubscription || !ravenService.onTrack) {
            return;
        }

        activeSubscription = ravenService.onTrack(appendRecord);
        disposes.push(activeSubscription);
    }

    function unsubscribe() {
        activeSubscription?.dispose();
        activeSubscription = undefined;
        records.value = [];
    }

    function clearRecords() {
        records.value = [];
    }

    function toggleExpanded() {
        isExpanded.value = !isExpanded.value;
    }

    watch(enabled, (value) => {
        if (value) {
            subscribe();
            return;
        }

        unsubscribe();
    }, { immediate: true });

    return {
        enabled,
        records: readonly(records),
        isExpanded: readonly(isExpanded),
        onRecord: onRecordEmitter.event,
        clearRecords,
        toggleExpanded,
    };
});
